// Client-side soft-redirect fallback for legacy axleinc.com (WordPress) URLs.
// Production 301s come from `public/_redirects` (server-side). This file ensures
// users still land on the right page if the static host doesn't honor the rules
// (e.g. dev preview), and during the SPA fallback render of unknown paths.

import { BLOG_REDIRECTS } from "../blog/redirects-data.ts";

export const exact: Record<string, string> = {
  ...BLOG_REDIRECTS,
  // WordPress / system
  "/home": "/",
  "/index.html": "/",
  "/index.php": "/",
  "/wp-login.php": "/",
  "/xmlrpc.php": "/",
  "/feed": "/blog/",
  "/feed/": "/blog/",
  "/__trashed/": "/blog/",

  // Trailing-slash canonicalization
  "/products": "/products/",
  "/about": "/about/",
  "/contact": "/contact/",
  "/blog": "/blog/",
  "/rebates": "/rebates/",
  "/dexter": "/dexter/",
  "/locations": "/locations/",
  "/products/hubs": "/products/hubs/",
  "/products/suspension-parts": "/products/suspension-parts/",
  "/products/jacks-couplers": "/products/jacks-couplers/",
  "/products/tires-wheels": "/products/tires-wheels/",
  "/products/fenders": "/products/",
  "/products/fenders/": "/products/",

  // Renamed pages
  "/services/mobile-trailer-repair": "/services/emergency-same-day-trailer-repair/",
  "/services/mobile-trailer-repair/": "/services/emergency-same-day-trailer-repair/",

  // Old service URLs
  "/service": "/services/axle-repair/",
  "/repair": "/services/axle-repair/",
  "/axle-repair": "/services/axle-repair/",
  "/axle-repair/": "/services/axle-repair/",
  "/axles": "/axle-order-forms/",
  "/axles/": "/axle-order-forms/",
  "/trailer-inspection-near-me/": "/services/axle-repair/",
  "/trailer-axle-repair-service-in-elkhart-in/": "/services/axle-repair/",
  "/utility-trailer-repair-in-elkhart-in/": "/services/axle-repair/",
  "/utility-trailer-repair-in-south-bend-in/": "/locations/south-bend-in/trailer-repair/",

  // Static legacy pages
  "/blog-2/": "/blog/",
  "/tandc/": "/",
  "/privacy/": "/",
  "/return-policy/": "/contact/",
  "/cade-faq/": "/contact/",
  "/home-by-rd/": "/",
  "/schedule-service/": "/contact/",
  "/al-ko-hayes-legacy-products/": "/dexter/",
  "/trailer-parts-store-in-elkhart-indiana/": "/products/",
  "/dexter-axle-elkhart-indiana-trusted-trailer-axle-service-parts/": "/dexter/",
  "/axle-rebate/": "/rebates/axle-rebate/",

  // Legacy blog posts → matching post or topic page
  "/dexter-trailer-axle-alternatives-top-2026-options/": "/blog/dexter-trailer-axle-alternatives-top-2026-options/",
  "/boat-trailer-repair-near-you-fast-local-experts/": "/services/axle-repair/",
  "/get-trailer-hubs-fast-shipping-guaranteed/": "/products/hubs/",
  "/leaf-spring-suspension-complete-guide-for-rv-trailers/": "/products/suspension-parts/",
  "/torsion-axles-explained-complete-guide-for-beginners/": "/products/suspension-parts/",
  "/trailer-brake-conversion-fast-professional-upgrade-kit/": "/blog/electric-vs-hydraulic-trailer-brakes/",
  "/best-heavy-duty-trailer-axles-for-2026-top-tandem-options/": "/blog/choosing-the-right-trailer-axle-capacity/",
  "/best-cargo-trailer-axles-for-2026-top-recommendations/": "/blog/choosing-the-right-trailer-axle-capacity/",
  "/best-3500-lb-trailer-axle-for-2026-top-options-reviewed/": "/blog/choosing-the-right-trailer-axle-capacity/",
  "/trailer-repair-indiana-local-experts-for-fast-fixes/": "/locations/",
  "/custom-trailer-axles-elkhart-in-professional-fast-installation/": "/products/",
  "/trailer-parts-store-plymouth-in-top-local-supplier-repair/": "/locations/plymouth-in/parts/",
  "/trailer-repair-granger-in-fast-local-rv-experts/": "/locations/granger-in/trailer-repair/",
  "/local-trailer-axle-repair-osceola-fast-and-reliable-service/": "/locations/osceola-in/trailer-repair/",
};

export const externalRedirects: Record<string, string> = {
  "/shop": "https://parts.axleinc.com/",
  "/shop/": "https://parts.axleinc.com/",
  "/parts": "https://parts.axleinc.com/",
  "/parts/": "https://parts.axleinc.com/",
};

// Cities that exist as their own /locations/{slug}/ page in the new site.
const SERVICED_CITIES = new Set([
  "granger-in",
  "mishawaka-in",
  "goshen-in",
  "south-bend-in",
  "bristol-in",
  "middlebury-in",
  "nappanee-in",
  "wakarusa-in",
  "new-paris-in",
  "plymouth-in",
  "warsaw-in",
  "osceola-in",
  "dunlap-in",
  "millersburg-in",
  "kendallville-in",
  "niles-mi",
  "sturgis-mi",
  "cassopolis-mi",
  "benton-harbor-mi",
  "kalamazoo-mi",
]);

// Map old WP service-prefix to a destination on the new site.
// Returns the local product slug ("trailer-repair", "trailer-axles", "parts"),
// "city" for "go to city home", or "dexter" for "go to /dexter/".
const SERVICE_PREFIXES: { prefix: string; dest: "trailer-repair" | "trailer-axles" | "parts" | "city" | "dexter" }[] = [
  { prefix: "full-service-trailer-repair", dest: "trailer-repair" },
  { prefix: "trailer-axle-replacement", dest: "trailer-repair" },
  { prefix: "trailer-suspension-repair", dest: "trailer-repair" },
  { prefix: "trailer-hub-bearing-service", dest: "trailer-repair" },
  { prefix: "trailer-brake-repair", dest: "trailer-repair" },
  { prefix: "trailer-axle-repair", dest: "trailer-repair" },
  { prefix: "trailer-repair", dest: "trailer-repair" },
  { prefix: "custom-trailer-axles", dest: "trailer-axles" },
  { prefix: "online-trailer-parts-ordering", dest: "parts" },
  { prefix: "trailer-parts-store", dest: "parts" },
  { prefix: "official-dexter-axle-distributor", dest: "dexter" },
  { prefix: "dexter-axle-parts", dest: "parts" },
  { prefix: "trailer-inspection", dest: "city" },
];

// Try to derive a target from the legacy `<service>-<city>-<state>-<digits>/` pattern.
export function patternMatch(path: string): string | null {
  // Strip leading/trailing slashes for matching.
  const slug = path.replace(/^\/+|\/+$/g, "");
  if (!slug) return null;

  // Auto-generated WP page IDs like "615349bc"
  if (/^\d{6}[a-z]{0,3}$/.test(slug)) return "/";

  // WordPress category pages → /blog/
  if (slug === "category" || slug.startsWith("category/")) return "/blog/";

  // City × service URLs end in `-<city>-<state>-<digits>`
  // (Some end in `-<city>-<state>-<long-suffix>`; we only auto-match the digit form.)
  for (const { prefix, dest } of SERVICE_PREFIXES) {
    const re = new RegExp(`^${prefix}-([a-z]+(?:-[a-z]+)*)-(in|mi)-\\d+$`);
    const m = slug.match(re);
    if (m) {
      const cityState = `${m[1]}-${m[2]}`;
      // Elkhart base: no /locations/ page - collapse to site-level destinations.
      if (cityState === "elkhart-in") {
        if (dest === "parts") return "/products/";
        if (dest === "trailer-axles") return "/products/";
        if (dest === "dexter") return "/dexter/";
        return "/services/axle-repair/";
      }
      if (!SERVICED_CITIES.has(cityState)) return "/locations/";
      if (dest === "city") return `/locations/${cityState}/`;
      if (dest === "dexter") return `/locations/${cityState}/`;
      return `/locations/${cityState}/${dest}/`;
    }
  }

  // Catch-all for any other legacy `{service}-{city}-{state}-{id}` slug we
  // don't have a specific mapping for → the locations index (never home).
  if (/^[a-z0-9-]+-(in|mi)-\d+$/.test(slug)) return "/locations/";

  return null;
}

/** Spanish aliases stay in Spanish; explicit /es URLs never redirect to English. */
export function localeRedirect(path: string): string | null {
  if (path === "/es") return "/es/";
  if (!path.startsWith("/es/")) return null;
  const english = path.slice(3);
  const destination = exact[english];
  return destination ? `/es${destination}` : null;
}

if (typeof window !== "undefined") {
  const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  const path = base && window.location.pathname.startsWith(`${base}/`)
    ? window.location.pathname.slice(base.length) : window.location.pathname;
  const localReplace = (target: string) => window.location.replace(base + target + window.location.search + window.location.hash);

  if (localeRedirect(path)) {
    localReplace(localeRedirect(path)!);
  } else if (path.startsWith("/wp-admin") || path.startsWith("/wp-content") || path.startsWith("/wp-includes")) {
    localReplace("/");
  } else if (exact[path]) {
    localReplace(exact[path]);
  } else if (externalRedirects[path]) {
    window.location.replace(externalRedirects[path]);
  } else if (path.startsWith("/shop/")) {
    window.location.replace("https://parts.axleinc.com/" + path.slice("/shop/".length) + window.location.search);
  } else if (path.startsWith("/parts/")) {
    window.location.replace("https://parts.axleinc.com/" + path.slice("/parts/".length) + window.location.search);
  } else {
    const target = patternMatch(path);
    if (target && target !== path) {
      localReplace(target);
    }
  }
}
